import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fieldInputCls } from "@/components/Field";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { fmtLap } from "@/lib/telemetry";

export function useCatalog(trackId) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/telemetry/catalog", { params: trackId ? { track_id: trackId } : {} }).then((r) => setItems(r.data));
  }, [trackId]);
  return items;
}

const dateIt = (d) => (d ? d.split("-").reverse().join("/") : "");
export const sessionLabel = (i) => [i.driver || "—", i.event_name, `${i.session_name}${i.stint === 2 ? " (PIT 2)" : ""}`, dateIt(i.date)].filter(Boolean).join(" · ");

export function lapLabel(items, sel) {
  const it = items?.find((i) => i.id === sel.telId);
  if (!it) return "";
  const k = it.laps.findIndex((l) => l.lap === sel.lap);
  return `${it.driver ? `${it.driver} · ` : ""}${it.session_name} · G${k + 1}`;
}

export function useRemoteLap(sel, items) {
  const [lap, setLap] = useState(null);
  useEffect(() => {
    if (!sel) {
      setLap(null);
      return undefined;
    }
    let live = true;
    api.get(`/telemetry/${sel.telId}/laps`, { params: { laps: sel.lap } }).then((r) => {
      if (live && r.data[0]) setLap({ ...r.data[0], label: lapLabel(items, sel) });
    });
    return () => {
      live = false;
    };
  }, [sel, items]);
  return lap;
}

export const LapPicker = ({ items, value, onChange, testid, color, label }) => {
  const { t } = useT();
  const item = items.find((i) => i.id === value?.telId);
  const bestId = items.reduce((m, i) => (i.best != null && (!m || i.best < m.best) ? i : m), null)?.id;
  const pickSession = (id) => {
    const it = items.find((i) => i.id === id);
    const best = it.laps.reduce((m, l) => (!m || l.time < m.time ? l : m), null);
    onChange(best ? { telId: id, lap: best.lap } : null);
  };
  return (
    <div data-testid={testid} className="space-y-1.5 min-w-0">
      <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color }}>{label}</span>
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_150px] gap-2">
        <Select value={value?.telId || ""} onValueChange={(id) => id && pickSession(id)}>
          <SelectTrigger data-testid={`${testid}-session`} className={`${fieldInputCls} [&>span]:truncate`}><SelectValue placeholder={t("pick_session")} /></SelectTrigger>
          <SelectContent>
            {items.map((i) => (
              <SelectItem key={i.id} value={i.id} data-testid={i.id === bestId ? `${testid}-best-session` : undefined}>
                <span className={i.id === bestId ? "text-[#10B981] font-bold" : ""}>{sessionLabel(i)} · {fmtLap(i.best)}{i.id === bestId ? ` ★ ${t("best_session")}` : ""}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={value ? String(value.lap) : ""} disabled={!item} onValueChange={(v) => v && value && onChange({ telId: value.telId, lap: Number(v) })}>
          <SelectTrigger data-testid={`${testid}-lap`} className={`${fieldInputCls} [&>span]:truncate`}><SelectValue placeholder={t("lap_col")} /></SelectTrigger>
          <SelectContent>
            {item?.laps.map((l, k) => <SelectItem key={l.lap} value={String(l.lap)}>G{k + 1} · {fmtLap(l.time)}{l.time === item.best ? " ★" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
